import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import {
  Window,
  WindowContent,
  WindowHeader,
  Table,
  TableBody,
  TableRow,
  TableHead,
  TableHeadCell,
  TableDataCell,
  GroupBox,
} from "react95";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { SOCIAL_LOGOS as LOGOS } from "../utils/socialLogos";
import SocialSharePopup from "../components/SocialSharePopup";
import CustomReact95Toast from "../components/CustomReact95Toast";
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const EXPLORER_BASE_URL = "https://explorer.solana.com/tx/";

/* =======================  STYLES  ======================= */
const PageWrapper = styled.div`
  padding-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledWindow = styled(Window)`
  width: 95%;
  max-width: 1200px;
`;

const WindowHeaderStyled = styled(WindowHeader)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px;
`;

const ScrollableTable = styled.div`
  max-height: 600px;
  overflow-y: auto;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

/* Row the user taps — full table on desktop, single-cell summary on mobile */
const SummaryRow = styled(TableRow)`
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: ${({ theme }) => theme.hoverBackground || "#f5f5f5"};
  }

  @media (max-width: 768px) {
    td:nth-child(n + 3) {
      display: none; /* hide all but first cell (ID) */
    }
  }
`;

/* Detail row appears directly below summary row when expanded — mobile only */
const DetailRow = styled(TableRow)`
  @media (min-width: 769px) {
    display: none; /* desktop never shows this row */
  }
  td {
    padding: 0; /* we’ll pad inside the card */
    border: none;
  }
`;

const DetailCard = styled.div`
  padding: 12px;
  background: ${({ theme }) => theme.canvas};
  border: 1px solid #d0d0d0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  font-size: 0.9rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 6px;
  column-gap: 8px;

  /* Single column on extra-small screens */
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }

  strong {
    font-weight: 600;
  }
`;

const StyledTable = styled(Table)`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  thead {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${({ theme }) => theme.material};
  }

  th,
  td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Make the ID column as wide as the longest ID (+1ch buffer) */
  th:first-child,
  td:first-child {
    width: ${({ $idWidth }) => $idWidth}ch;
  }

  @media (max-width: 768px) {
    thead {
      display: none;
      table-layout: auto;
    }
  }
`;

const LogoContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const LogoImage = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.2);
  }
`;

/* =======================  HELPERS  ======================= */
const formatTime = (ms) => {
  if (ms <= 0) return "00:00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const h = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
  const s = String(totalSeconds % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
};

const truncateTx = (tx) => `${tx.slice(0, 4)}...${tx.slice(-4)}`;
const renderTxLink = (tx) =>
  !tx || tx === "-" ? (
    "-"
  ) : (
    <a
      href={`${EXPLORER_BASE_URL}${tx}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: "none" }}
    >
      {truncateTx(tx)}
    </a>
  );

/* ====================  COMPONENT  ==================== */
const CompetitionsListPage = () => {
  const navigate = useNavigate();

  const [competitions, setCompetitions] = useState([]);
  const [extraData, setExtraData] = useState({});
  const [expanded, setExpanded] = useState(null); // id of open row (mobile)
  const [now, setNow] = useState(Date.now());
  const [showSocialShare, setShowSocialShare] = useState(false);
  const [shareCompetition, setShareCompetition] = useState(null);

  /* ---------- timers ---------- */
  useEffect(() => {
    const int = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(int);
  }, []);

  /* ---------- load competitions ---------- */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/competitions`);
        const sorted = [...data].sort(
          (a, b) =>
            new Date(b.createdAt || b.created_at) -
            new Date(a.createdAt || a.created_at)
        );
        setCompetitions(sorted);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  /* ---------- compute longest ID ---------- */
  const longestIdChars = useMemo(
    () => competitions.reduce((n, c) => Math.max(n, String(c.id).length), 0),
    [competitions]
  );

  /* ---------- load per-competition extra ---------- */
  useEffect(() => {
    if (!competitions.length) return;
    (async () => {
      const results = await Promise.all(
        competitions.map(async (c) => {
          try {
            const [entries, txs] = await Promise.all([
              axios.get(`${API_BASE_URL}/competitions/${c.id}/entries`),
              axios.get(`${API_BASE_URL}/competitions/${c.id}/transactions`),
            ]);
            return {
              id: c.id,
              participants: new Set(entries.data.map((e) => e.user_id)).size,
              txs: txs.data,
            };
          } catch (e) {
            console.error(e);
            return { id: c.id, participants: 0, txs: [] };
          }
        })
      );
      const map = {};
      results.forEach((r) => (map[r.id] = r));
      setExtraData(map);
    })();
  }, [competitions]);

  /* ---------- helpers ---------- */
  const getTx = (arr, type) => {
    const tx = arr.find(
      (t) =>
        t.type === type &&
        (t.txID || t.txSignature || t.signature || t.details?.txSignature)
    );
    return tx
      ? tx.txID || tx.txSignature || tx.signature || tx.details?.txSignature
      : "-";
  };

  const statusText = (c) => {
    const diff = new Date(c.end_time).getTime() - now;
    if (diff > 0 && c.status === "active") return formatTime(diff);
    if (c.status === "processing") return "Selecting Winner";
    if (c.status === "completed") return "Completed";
    if (c.status === "failed") return "Failed";
    return "Ended";
  };

  const toggleMobile = (id) => setExpanded((prev) => (prev === id ? null : id));
  const formatURL = (url) => {
    if (!url) return "";
    const lower = url.toLowerCase();
    return lower.startsWith("http://") || lower.startsWith("https://")
      ? url
      : `https://${url}`;
  };
  const truncateAddress = (addr) =>
    addr ? `${addr.slice(0, 5)}...${addr.slice(-5)}` : "";

  const handleShareClick = (competition) => {
    setShareCompetition(competition);
    setShowSocialShare(true);
  };
  const handleCloseSocialShare = () => setShowSocialShare(false);

  const handleCopySuccess = (msg) => {
    toast(
      (tp) => (
        <CustomReact95Toast
          title="Copied"
          message={msg}
          closeToast={tp.closeToast}
        />
      ),
      {
        position: "top-right",
        autoClose: 5000,
        closeOnClick: false,
        closeButton: false,
        hideProgressBar: false,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
      }
    );
    setShowSocialShare(false);
  };

  /* ====================  RENDER  ==================== */
  return (
    <PageWrapper>
      <StyledWindow>
        <WindowHeaderStyled>
          <span>Competitions.exe</span>
          <button onClick={() => navigate(-1)}>X</button>
        </WindowHeaderStyled>
        <WindowContent>
          <GroupBox label="All Competitions">
            <ScrollableTable>
              <StyledTable $idWidth={longestIdChars + 1}>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>ID</TableHeadCell>
                    <TableHeadCell>Name</TableHeadCell>
                    <TableHeadCell>Ticker</TableHeadCell>
                    <TableHeadCell>CA</TableHeadCell>
                    <TableHeadCell>Jackpot</TableHeadCell>
                    <TableHeadCell>Status</TableHeadCell>
                    <TableHeadCell>End Tx</TableHeadCell>
                    <TableHeadCell>VRF Tx</TableHeadCell>
                    <TableHeadCell>Winner Tx</TableHeadCell>
                    <TableHeadCell>Winner</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {competitions.map((c) => {
                    const extra = extraData[c.id] || {
                      participants: 0,
                      txs: [],
                    };
                    const open = expanded === c.id;

                    return (
                      <React.Fragment key={c.id}>
                        <SummaryRow onClick={() => toggleMobile(c.id)}>
                          <TableDataCell>{c.id}</TableDataCell>
                          <TableDataCell>
                            {c.header || c.token_name}
                          </TableDataCell>
                          <TableDataCell>{c.token_symbol}</TableDataCell>
                          <TableDataCell>
                            {truncateAddress(c.token)}
                          </TableDataCell>
                          <TableDataCell>
                            {Number(c.prize_pool).toFixed(2)} SOL
                          </TableDataCell>
                          <TableDataCell>{statusText(c)}</TableDataCell>
                          <TableDataCell>
                            {renderTxLink(getTx(extra.txs, "Competition End"))}
                          </TableDataCell>
                          <TableDataCell>
                            {renderTxLink(
                              getTx(extra.txs, "Request Randomness")
                            )}
                          </TableDataCell>
                          <TableDataCell>
                            {renderTxLink(getTx(extra.txs, "Select Winner"))}
                          </TableDataCell>
                          <TableDataCell>
                            {c.winning_ticket || "-"}
                          </TableDataCell>
                        </SummaryRow>

                        {open && (
                          <DetailRow>
                            <TableDataCell colSpan="12">
                              <DetailCard>
                                <strong>Jackpot</strong>
                                <span>
                                  {Number(c.prize_pool).toFixed(2)} SOL
                                </span>
                                <strong>Status / Time</strong>
                                <span>{statusText(c)}</span>
                                <strong>Token Address</strong>
                                <span>{truncateAddress(c.token)}</span>
                                <strong>End Tx</strong>
                                <span>
                                  {renderTxLink(
                                    getTx(extra.txs, "Competition End")
                                  )}
                                </span>
                                <strong>VRF Tx</strong>
                                <span>
                                  {renderTxLink(
                                    getTx(extra.txs, "Request Randomness")
                                  )}
                                </span>
                                <strong>Winner Tx</strong>
                                <span>
                                  {renderTxLink(
                                    getTx(extra.txs, "Select Winner")
                                  )}
                                </span>
                                <strong>Winner</strong>
                                <span>{c.winning_ticket || "-"}</span>

                                {/* ------- social / external links ------- */}
                                <LogoContainer>
                                  {LOGOS.map((logo, idx) => {
                                    const link = c[logo.linkField];
                                    if (!link && !logo.isShare) return null;

                                    if (logo.isShare) {
                                      return (
                                        <LogoImage
                                          key={idx}
                                          src={logo.src}
                                          alt="share"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleShareClick(c);
                                          }}
                                        />
                                      );
                                    }

                                    return (
                                      <a
                                        key={idx}
                                        href={formatURL(link)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <LogoImage
                                          src={logo.src}
                                          alt={logo.linkField}
                                        />
                                      </a>
                                    );
                                  })}
                                </LogoContainer>
                              </DetailCard>
                            </TableDataCell>
                          </DetailRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </StyledTable>
            </ScrollableTable>
          </GroupBox>
        </WindowContent>
      </StyledWindow>

      {showSocialShare && shareCompetition && (
        <SocialSharePopup
          competition={shareCompetition}
          onClose={handleCloseSocialShare}
          onCopy={handleCopySuccess}
        />
      )}
    </PageWrapper>
  );
};

export default CompetitionsListPage;
